import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GalleryGroupRarityCounts } from 'shared/components/RarityCountsDisplay';
import { useTalosData } from 'shared/context/TalosDataContext';
import { Badge } from 'features/catalog/useBadges';
import type { BadgeStatKey } from 'features/catalog/badge-definitions';
import {
  ARENA_MAX_LOSSES,
  ARENA_SCHEDULE,
  ARENA_WINS_TO_COMPLETE,
  BATTLE_DECK_SIZE,
  CPU_DIFFICULTIES,
  CPU_DIFFICULTY_LABELS,
  createBattle,
  cpuDifficultyUnlockHint,
  cpuName,
  arenaOpponentQueue,
  deckIdsMatch,
  deckPowerGames,
  isCpuDifficultyUnlocked,
  listSavedBattleDecks,
  nextArenaOffer,
  rateDeckPowerAsync,
  recordBattleWin,
  recordCpuDifficultyWin,
  saveBattleDeck,
  sortBattleDeckIds,
  sampleBattleDeckById,
  type CpuDifficulty,
} from 'features/battle/index';
import { RARITY_TIER_LABELS } from 'features/catalog/rarity';
import {
  BadgesBattleView,
  BattleBadge,
  BattleOpenBadgeProvider,
  BattlePageHeader,
  DeckGroupFill,
  DeckGroupSwatch,
  DeckStats,
  sumDeckStats,
} from 'features/battle/views/BadgesBattleView';
import '../../gallery/BadgeGalleryView.css';
import './BadgesBattleView.css';
import './BattleArenaView.css';

const ArenaPageHeader: React.FC<{
  actions?: React.ReactNode;
}> = ({ actions }) => <BattlePageHeader title="Battle Arena" actions={actions} />;

const ArenaRecord: React.FC<{ wins: number; losses: number; highlight?: 'wins' | 'losses' }> = ({
  wins,
  losses,
  highlight,
}) => (
  <div className="bb-arena-record" aria-label={`${wins} wins, ${losses} losses`}>
    <div className={`bb-arena-record-side is-wins${highlight === 'wins' ? ' is-highlight' : ''}`}>
      <span className="bb-arena-record-label">Wins</span>
      <div className="bb-arena-record-pips">
        {Array.from({ length: ARENA_WINS_TO_COMPLETE }, (_, index) => (
          <b key={index} className={index < wins ? 'is-on' : ''} />
        ))}
      </div>
    </div>
    <div className={`bb-arena-record-side is-losses${highlight === 'losses' ? ' is-highlight' : ''}`}>
      <span className="bb-arena-record-label">Losses</span>
      <div className="bb-arena-record-pips">
        {Array.from({ length: ARENA_MAX_LOSSES }, (_, index) => (
          <b key={index} className={index < losses ? 'is-loss' : ''} />
        ))}
      </div>
    </div>
  </div>
);

const shownDifficultyRates = (
  rates: Partial<Record<CpuDifficulty, number>>
): Partial<Record<CpuDifficulty, number>> => {
  const shown: Partial<Record<CpuDifficulty, number>> = {};
  let cap: number | undefined;
  for (const level of CPU_DIFFICULTIES) {
    const raw = rates[level];
    if (raw == null) continue;
    const next = cap == null ? raw : Math.min(raw, cap);
    shown[level] = next;
    cap = next;
  }
  return shown;
};

type Phase = 'draft' | 'difficulty' | 'run' | 'match' | 'done';

type Props = {
  playerName: string;
  playerId?: string;
  ownedBadgeIds: string[];
  profileId?: string | null;
  onSelectBadge?: (badge: Badge) => void;
};

const newSeed = () => Date.now() % 1_000_000 || 1;

const ArenaPickGroup: React.FC<{
  title: string;
  badgeIds: readonly string[];
  badgesMap: Map<string, Badge>;
  dealIndex: number;
  onChoose: () => void;
  onHoverChoose?: (badges: Badge[]) => void;
  onLeaveChoose?: () => void;
}> = ({ title, badgeIds, badgesMap, dealIndex, onChoose, onHoverChoose, onLeaveChoose }) => {
  const badges = badgeIds.map((id) => badgesMap.get(id)).filter((badge): badge is Badge => Boolean(badge));
  return (
    <section
      className="gallery-group bb-arena-pick"
      style={{ '--bb-deal-pack': dealIndex, '--bb-deal-count': badges.length } as React.CSSProperties}
    >
      <div className="gallery-group-content">
        <header className="gallery-group-head">
          <span className="gallery-group-swatch gallery-group-swatch--muted" aria-hidden />
          <div className="gallery-group-titles">
            <h2 className="gallery-group-name">{title}</h2>
          </div>
          <GalleryGroupRarityCounts badgeIds={[...badgeIds]} badgesMap={badgesMap} />
          <button
            type="button"
            className="bb-start"
            onClick={onChoose}
            onPointerEnter={() => onHoverChoose?.(badges)}
            onPointerLeave={() => onLeaveChoose?.()}
          >
            Choose
          </button>
          <DeckStats totals={sumDeckStats(badges)} borderless trio />
        </header>
        <div className="gallery-badge-grid bb-arena-trio-grid">
          {badges.map((badge, badgeIndex) => (
            <BattleBadge key={badge.id} badge={badge} compact dealIndex={badgeIndex} />
          ))}
        </div>
      </div>
    </section>
  );
};

const ArenaYours: React.FC<{
  deck: string[];
  deckBadges: Badge[];
  badgesMap: Map<string, Badge>;
  dealIds?: readonly string[];
  previewTotals?: Record<BadgeStatKey, number> | null;
  onSelectBadge?: (badge: Badge) => void;
}> = ({ deck, deckBadges, badgesMap, dealIds = [], previewTotals, onSelectBadge }) => {
  const totals = sumDeckStats(deckBadges);
  return (
    <section className="gallery-group bb-arena-yours">
      <DeckGroupFill totals={totals} count={deck.length} />
      <div className="gallery-group-content">
        <header className="gallery-group-head">
          <DeckGroupSwatch totals={totals} count={deck.length} />
          <div className="gallery-group-titles">
            <h2 className="gallery-group-name">Your Deck</h2>
            <p className="gallery-group-meta">
              {deck.length}/{BATTLE_DECK_SIZE}
            </p>
          </div>
          <GalleryGroupRarityCounts badgeIds={deck} badgesMap={badgesMap} />
        </header>
        <div className="gallery-badge-grid">
          {Array.from({ length: BATTLE_DECK_SIZE }, (_, index) => {
            const badge = deckBadges[index];
            if (!badge) {
              return (
                <div key={`empty-${index}`} className="gallery-badge bb-slot">
                  <span>{index + 1}</span>
                </div>
              );
            }
            const dealI = dealIds.indexOf(badge.id);
            return (
              <BattleBadge
                key={badge.id}
                badge={badge}
                compact
                onClick={onSelectBadge}
                dealIndex={dealI >= 0 ? dealI : undefined}
              />
            );
          })}
        </div>
        <DeckStats totals={totals} preview={previewTotals} borderless />
      </div>
    </section>
  );
};

export const BattleArenaView: React.FC<Props> = ({ playerName, playerId, ownedBadgeIds, profileId, onSelectBadge }) => {
  const { badgesMap } = useTalosData();
  const [seed, setSeed] = useState(newSeed);
  const [deck, setDeck] = useState<string[]>([]);
  const [pickIndex, setPickIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('draft');
  const [difficulty, setDifficulty] = useState<CpuDifficulty>('easy');
  const [wins, setWins] = useState(0);
  const [losses, setLosses] = useState(0);
  const [played, setPlayed] = useState(0);
  const [matchSeed, setMatchSeed] = useState(1);
  const [afterMatch, setAfterMatch] = useState<Phase>('run');
  const afterMatchRef = useRef<Phase>(afterMatch);
  afterMatchRef.current = afterMatch;
  const [abandonOpen, setAbandonOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [deckSaved, setDeckSaved] = useState(false);
  const [difficultyRates, setDifficultyRates] = useState<Partial<Record<CpuDifficulty, number>>>({});
  const [hoverDraft, setHoverDraft] = useState<Badge[]>([]);
  const [dealIds, setDealIds] = useState<string[]>([]);
  const opponents = useMemo(() => arenaOpponentQueue(seed), [seed]);
  const offer = useMemo(
    () => (phase === 'draft' ? nextArenaOffer(deck, pickIndex, seed) : null),
    [deck, phase, pickIndex, seed]
  );
  const deckBadges = useMemo(
    () =>
      sortBattleDeckIds(deck)
        .map((id) => badgesMap.get(id))
        .filter((badge): badge is Badge => Boolean(badge)),
    [badgesMap, deck]
  );
  const previewTotals = useMemo(
    () => (hoverDraft.length ? sumDeckStats([...deckBadges, ...hoverDraft]) : null),
    [deckBadges, hoverDraft]
  );

  useEffect(() => {
    setHoverDraft([]);
  }, [pickIndex, seed]);

  useEffect(() => {
    if (!dealIds.length) return;
    const timer = window.setTimeout(() => setDealIds([]), 1200);
    return () => window.clearTimeout(timer);
  }, [dealIds]);
  const opponent = sampleBattleDeckById(opponents[played % opponents.length] ?? opponents[0]);
  const displayRates = useMemo(() => shownDifficultyRates(difficultyRates), [difficultyRates]);

  useEffect(() => {
    if (phase !== 'difficulty' || deck.length !== BATTLE_DECK_SIZE) {
      setDifficultyRates({});
      return;
    }
    let cancelled = false;
    setDifficultyRates({});
    const unlocked = CPU_DIFFICULTIES.filter((level) => level !== 'impossible').filter((level) =>
      isCpuDifficultyUnlocked(level, 'arena')
    );
    let index = 0;
    let timer = 0;
    const runNext = () => {
      if (cancelled) return;
      const level = unlocked[index];
      if (!level) return;
      void rateDeckPowerAsync(deck, deckPowerGames(level), level, () => cancelled).then((percent) => {
        if (cancelled || percent == null) return;
        setDifficultyRates((current) => ({ ...current, [level]: percent }));
        index += 1;
        if (index < unlocked.length) timer = window.setTimeout(runNext, 0);
      });
    };
    timer = window.setTimeout(runNext, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [deck, phase]);

  const resetDraft = () => {
    setSeed(newSeed());
    setDeck([]);
    setPickIndex(0);
    setPhase('draft');
    setWins(0);
    setLosses(0);
    setPlayed(0);
    setSaveOpen(false);
    setSaveName('');
    setDeckSaved(false);
    setDealIds([]);
    setHoverDraft([]);
    afterMatchRef.current = 'run';
    setAfterMatch('run');
  };

  const openSaveDeck = () => {
    const matching = listSavedBattleDecks().find((saved) => deckIdsMatch(saved.badgeIds, deck));
    setSaveName(matching?.name ?? `Arena ${CPU_DIFFICULTY_LABELS[difficulty]}`);
    setSaveOpen(true);
  };

  const submitSaveDeck = (event: React.FormEvent) => {
    event.preventDefault();
    const saved = saveBattleDeck(saveName, deck);
    if (!saved) return;
    setDeckSaved(true);
    setSaveOpen(false);
  };

  const addToDeck = (ids: string[]) => {
    const next = sortBattleDeckIds([...deck, ...ids]);
    const nextPick = pickIndex + 1;
    setDealIds(ids);
    setDeck(next);
    if (nextPick >= ARENA_SCHEDULE.length) {
      setPickIndex(nextPick);
      setPhase('difficulty');
      return;
    }
    setPickIndex(nextPick);
  };

  const startGame = () => {
    setMatchSeed(newSeed());
    setPhase('match');
  };

  const requestAbandon = () => setAbandonOpen(true);
  const confirmAbandon = () => {
    setAbandonOpen(false);
    resetDraft();
  };

  if (phase === 'match' && opponent) {
    const gameNumber = wins + losses + 1;
    return (
      <BadgesBattleView
        playerName={playerName}
        playerId={playerId}
        ownedBadgeIds={ownedBadgeIds}
        profileId={profileId}
        onSelectBadge={onSelectBadge}
        preset={{
          create: (nextSeed) =>
            createBattle({
              decks: [deck, [...opponent.badgeIds]],
              names: [playerName, cpuName(difficulty)],
              seed: nextSeed || matchSeed,
              cpu: 1,
              cpuDifficulty: difficulty,
              coinToss: true,
            }),
          difficulty,
          title: 'Battle Arena',
          label: `Game ${gameNumber} · ${CPU_DIFFICULTY_LABELS[difficulty]}`,
          exitLabel: 'Concede',
          forfeitOnLeave: true,
          leaveConfirm: {
            title: 'Concede',
            body: 'Concede this battle? It counts as a loss.',
            confirm: 'Concede',
          },
          onGameOver: (state) => {
            const won = state.winner === 0;
            const nextWins = wins + (won ? 1 : 0);
            const nextLosses = losses + (!won && state.winner !== 'tie' ? 1 : 0);
            if (won) recordCpuDifficultyWin(difficulty, 'arena');
            if (won && nextWins === ARENA_WINS_TO_COMPLETE && profileId) {
              recordBattleWin({
                profileId,
                name: playerName,
                gameId: `${matchSeed}-${Date.now()}`,
                kind: 'arena_win',
                difficulty,
                runComplete: true,
              });
            }
            setWins(nextWins);
            setLosses(nextLosses);
            setPlayed((n) => n + 1);
            const nextPhase: Phase =
              nextWins >= ARENA_WINS_TO_COMPLETE || nextLosses >= ARENA_MAX_LOSSES ? 'done' : 'run';
            afterMatchRef.current = nextPhase;
            setAfterMatch(nextPhase);
          },
          resultActions: () => (
            <button type="button" onClick={() => setPhase(afterMatchRef.current)}>
              Continue
            </button>
          ),
          onExit: () => setPhase(afterMatchRef.current),
        }}
      />
    );
  }

  return (
    <BattleOpenBadgeProvider onOpenBadge={onSelectBadge}>
      <div className="bb-page bb-arena is-setup">
        {phase === 'draft' && offer ? (
          <>
            <ArenaPageHeader />
            <div className="bb-arena-draft">
              <ArenaYours
                deck={deck}
                deckBadges={deckBadges}
                badgesMap={badgesMap}
                dealIds={dealIds}
                previewTotals={previewTotals}
                onSelectBadge={onSelectBadge}
              />
              <div
                key={`${pickIndex}-${seed}`}
                className={`bb-arena-offers is-${offer.kind === 'trio' ? 'trios' : 'singles'}`}
              >
                {offer.kind === 'single' ? (
                  <section className="gallery-group bb-arena-singles">
                    <div className="gallery-group-content">
                      <header className="gallery-group-head">
                        <span className="gallery-group-swatch gallery-group-swatch--muted" aria-hidden />
                        <div className="gallery-group-titles">
                          <h2 className="gallery-group-name">Pick a {RARITY_TIER_LABELS[offer.rarity]} badge</h2>
                        </div>
                      </header>
                      <div className="gallery-badge-grid bb-arena-singles-grid">
                        {offer.options.map((id, index) => {
                          const badge = badgesMap.get(id);
                          if (!badge) return null;
                          return (
                            <div
                              key={id}
                              className="bb-arena-deal"
                              style={{ '--bb-deal-i': index } as React.CSSProperties}
                              onPointerEnter={() => setHoverDraft([badge])}
                              onPointerLeave={() => setHoverDraft([])}
                            >
                              <BattleBadge badge={badge} compact onClick={() => addToDeck([id])} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </section>
                ) : (
                  offer.options.map((trio, index) => (
                    <ArenaPickGroup
                      key={trio.ids.join('-')}
                      title={trio.label}
                      badgeIds={trio.ids}
                      badgesMap={badgesMap}
                      dealIndex={index}
                      onChoose={() => addToDeck(trio.ids)}
                      onHoverChoose={setHoverDraft}
                      onLeaveChoose={() => setHoverDraft([])}
                    />
                  ))
                )}
              </div>
            </div>
          </>
        ) : null}

        {phase === 'difficulty' ? (
          <>
            <ArenaPageHeader />
            <div className="bb-arena-draft is-cpu">
              <ArenaYours
                deck={deck}
                deckBadges={deckBadges}
                badgesMap={badgesMap}
                dealIds={dealIds}
                onSelectBadge={onSelectBadge}
              />
              <div className="bb-arena-cpu-pick">
                <div className="bb-arena-difficulties">
                  {CPU_DIFFICULTIES.map((level) => {
                    const locked = !isCpuDifficultyUnlocked(level, 'arena');
                    const hint = cpuDifficultyUnlockHint(level, 'arena');
                    const hintId = `bb-arena-difficulty-tip-${level}`;
                    const rate = displayRates[level];
                    const unknown = level === 'impossible' && displayRates.hard != null;
                    return (
                      <div
                        key={level}
                        className={`bb-arena-cpu-wrap${locked ? ' is-locked' : ''}`}
                        aria-describedby={hint ? hintId : undefined}
                      >
                        <button
                          type="button"
                          disabled={locked}
                          className={`bb-arena-cpu is-${level}${locked ? ' is-locked' : ''}`}
                          aria-label={
                            locked
                              ? (hint ?? `${CPU_DIFFICULTY_LABELS[level]}, win rate hidden`)
                              : unknown
                                ? 'Impossible, win rate unknown'
                                : rate == null
                                  ? `${CPU_DIFFICULTY_LABELS[level]}, estimating win rate`
                                  : `${CPU_DIFFICULTY_LABELS[level]}, ${rate}% estimated win rate`
                          }
                          onClick={() => {
                            if (locked) return;
                            setDifficulty(level);
                            setPhase('run');
                          }}
                        >
                          <strong>{CPU_DIFFICULTY_LABELS[level]}</strong>
                          <span className={`bb-arena-cpu-rate${unknown ? ' bb-win-rate-unknown' : ''}`}>
                            {unknown ? (
                              '??%'
                            ) : locked ? (
                              '? %'
                            ) : rate == null ? (
                              <span className="bb-arena-cpu-loader" aria-hidden />
                            ) : (
                              `${rate}%`
                            )}
                          </span>
                        </button>
                        {hint ? (
                          <span className="bb-deck-pure-tip" id={hintId} role="tooltip">
                            {hint}
                          </span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
                <button type="button" className="bb-resign bb-arena-redraft" onClick={resetDraft}>
                  Draft Another
                </button>
              </div>
            </div>
          </>
        ) : null}

        {phase === 'run' && opponent ? (
          <>
            <ArenaPageHeader
              actions={
                <>
                  <button type="button" className={`bb-start is-${difficulty}`} onClick={startGame}>
                    Start game {wins + losses + 1}
                  </button>
                  <button type="button" className="bb-resign" onClick={requestAbandon}>
                    Abandon run
                  </button>
                </>
              }
            />
            <div className="bb-arena-draft">
              <ArenaYours deck={deck} deckBadges={deckBadges} badgesMap={badgesMap} onSelectBadge={onSelectBadge} />
              <ArenaRecord wins={wins} losses={losses} />
            </div>
          </>
        ) : null}

        {phase === 'done' ? (
          <>
            <ArenaPageHeader
              actions={
                <>
                  <button type="button" className="bb-recommend" onClick={openSaveDeck}>
                    {deckSaved ? 'Saved' : 'Save deck'}
                  </button>
                  <button type="button" className="bb-start" onClick={resetDraft}>
                    Draft again
                  </button>
                </>
              }
            />
            <div className="bb-arena-draft">
              <ArenaYours deck={deck} deckBadges={deckBadges} badgesMap={badgesMap} onSelectBadge={onSelectBadge} />
              <div className={`bb-arena-finish${wins >= ARENA_WINS_TO_COMPLETE ? ' is-win' : ' is-loss'}`}>
                {wins >= ARENA_WINS_TO_COMPLETE ? (
                  <>
                    <h2 className="bb-arena-finish-title">Congratulations!</h2>
                    <p className="bb-arena-finish-sub">
                      You won the Arena against {CPU_DIFFICULTY_LABELS[difficulty]} CPU, {wins}–{losses}.
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="bb-arena-finish-title">Run over</h2>
                    <p className="bb-arena-finish-sub">
                      {CPU_DIFFICULTY_LABELS[difficulty]} CPU took the Arena, {wins}–{losses}.
                    </p>
                  </>
                )}
                <ArenaRecord
                  wins={wins}
                  losses={losses}
                  highlight={wins >= ARENA_WINS_TO_COMPLETE ? 'wins' : 'losses'}
                />
              </div>
            </div>
          </>
        ) : null}

        {saveOpen ? (
          <div className="bb-overlay" onClick={() => setSaveOpen(false)}>
            <form
              className="bb-modal"
              role="dialog"
              aria-label="Save deck"
              onClick={(event) => event.stopPropagation()}
              onSubmit={submitSaveDeck}
            >
              <h2>Save deck</h2>
              <p>This deck will show up under Battle Badges.</p>
              <input
                value={saveName}
                onChange={(event) => setSaveName(event.target.value)}
                placeholder="Name this deck"
                maxLength={48}
                autoFocus
                aria-label="Deck name"
              />
              <button type="submit" disabled={!saveName.trim()}>
                Save
              </button>
              <button type="button" className="is-ghost" onClick={() => setSaveOpen(false)}>
                Cancel
              </button>
            </form>
          </div>
        ) : null}

        {abandonOpen ? (
          <div className="bb-overlay" onClick={() => setAbandonOpen(false)}>
            <div
              className="bb-modal"
              role="dialog"
              aria-label="Abandon run"
              onClick={(event) => event.stopPropagation()}
            >
              <h2>Abandon run</h2>
              <p>Are you sure you want to abandon this Arena run?</p>
              <button type="button" onClick={confirmAbandon} autoFocus>
                Abandon
              </button>
              <button type="button" className="is-ghost" onClick={() => setAbandonOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </BattleOpenBadgeProvider>
  );
};
